// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Link, useNavigate } from "@tanstack/react-router";
import type { ColumnDef, PaginationState, SortingState } from "@tanstack/react-table";
import { Construction, Filter, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "@/api/client";
import {
  type EtlProcessing,
  type EtlProcessingsResult,
  type EtlSortBy,
  useTransformationsQuery,
} from "@/api/hooks/use-imports";
import { DataTable } from "@/components/data-table";
import { DatabasePlusIcon } from "@/components/icons";
import { PageHeader } from "@/components/layout/page-header";
import { ChainLink } from "@/components/shared/chain-link";
import { CopyableKey } from "@/components/shared/copyable-key";
import { DateValue } from "@/components/shared/datetime-value";
import { FilterSelect } from "@/components/shared/filter-select";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { ImportTypeBadge } from "@/components/shared/import-type-badge";
import { KeyChip } from "@/components/shared/key-chip";
import { RowId } from "@/components/shared/row-id";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { StoreLink } from "@/components/shared/store-link";
import { Button } from "@/components/ui/button";
import { ImportDataDialog } from "@/features/imports/import-data-dialog";
import { ToggleEtlActiveDialog } from "@/features/ingress/toggle-etl-active-dialog";
import { TransformationActionsMenu } from "@/features/ingress/transformation-actions-menu";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useEscapeToClearFilters } from "@/hooks/use-escape-to-clear-filters";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { downloadCsv } from "@/lib/csv";
import { useDebugMode } from "@/lib/debug-mode";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { getDefaultPageSize } from "@/lib/preferences";
import { Route } from "@/routes/_authenticated/ingress/transformations/index";

type TransformationRow = EtlProcessing & {
  chainName: string;
  chainLegibleName: string;
  storeName: string;
  storeLegibleName: string;
};

const FILTER_DEFAULTS = {
  search: "",
  onlyActive: true,
  chain: "",
  store: "",
  page: 0,
  get pageSize() {
    return getDefaultPageSize();
  },
  sortId: "etlProcessingId",
  sortDesc: false,
};

export function TransformationsList() {
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.ingressTransformationsEdit);
  const { debugMode } = useDebugMode();
  const navigate = useNavigate();
  const { data: context, isLoading: contextLoading } = useBackofficeContext();
  const { f } = Route.useSearch();
  const initial = useMemo(() => decodeFilters(f, FILTER_DEFAULTS), [f]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initial.page,
    pageSize: initial.pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: initial.sortId, desc: initial.sortDesc }]);
  const [search, setSearch] = useState(initial.search);
  const [toggleEtl, setToggleEtl] = useState<TransformationRow | null>(null);
  const [importEtl, setImportEtl] = useState<TransformationRow | null>(null);

  const sortBy = (sorting[0]?.id ?? "etlProcessingId") as EtlSortBy;
  const orderBy = sorting[0]?.desc ? ("desc" as const) : ("asc" as const);

  const [onlyActive, setOnlyActive] = useState(initial.onlyActive);
  const [chainFilter, setChainFilter] = useState(initial.chain);
  const [storeFilter, setStoreFilter] = useState(initial.store);

  const isFiltered = !!(search || chainFilter || storeFilter || !onlyActive);

  const clearFilters = useCallback(function clearFilters() {
    setSearch("");
    setChainFilter("");
    setStoreFilter("");
    setOnlyActive(true);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEscapeToClearFilters({ isFiltered, clearFilters, disabled: toggleEtl !== null });

  const syncUrl = useCallback(
    (overrides: Partial<typeof FILTER_DEFAULTS> = {}) => {
      const current = {
        search,
        onlyActive,
        chain: chainFilter,
        store: storeFilter,
        page: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sortId: sorting[0]?.id ?? "etlProcessingId",
        sortDesc: sorting[0]?.desc ?? false,
        ...overrides,
      };
      const encoded = encodeFilters(current, FILTER_DEFAULTS);
      navigate({
        to: "/ingress/transformations",
        search: encoded ? { f: encoded } : {},
        replace: true,
      });
    },
    [search, onlyActive, chainFilter, storeFilter, pagination.pageIndex, pagination.pageSize, sorting, navigate],
  );

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  const { data, isLoading, refetch, isFetching } = useTransformationsQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
    sortBy,
    orderBy,
    onlyActive: onlyActive || undefined,
    chainName: chainFilter || undefined,
    storeName: storeFilter || undefined,
  });

  const handleExport = useCallback(async () => {
    const res = await api.GET("/v1/ingress/transformations/", {
      params: {
        query: {
          page: 1,
          limit: -1,
          search: search || undefined,
          sortBy,
          orderBy,
          "filter[chainName]": chainFilter || undefined,
          "filter[storeName]": storeFilter || undefined,
          "filter[onlyActive]": onlyActive || undefined,
        },
      },
    });
    const result = res.data as unknown as EtlProcessingsResult;
    const cMap = new Map((result.chains ?? []).map((c) => [c.chainId, c]));
    const sMap = new Map((result.stores ?? []).map((s) => [s.storeId, s]));
    downloadCsv(
      "transformations.csv",
      ["ID", "Name", "Description", "Chain", "Store", "Output Types", "Per-Store", "Status", "Created"],
      (result.etlProcessings ?? []).map((etl) => [
        String(etl.etlProcessingId),
        etl.shortName,
        etl.description,
        (etl.chainId != null ? cMap.get(etl.chainId)?.legibleName : "") ?? "",
        (etl.storeId != null ? sMap.get(etl.storeId)?.legibleName : "") ?? "",
        etl.outputImportTypes.join(", "),
        etl.isAvailablePerStore ? "Yes" : "No",
        etl.isActive ? "Active" : "Inactive",
        etl.createdAt,
      ]),
    );
  }, [search, sortBy, orderBy, chainFilter, storeFilter, onlyActive]);

  const chainMap = useMemo(() => new Map((data?.chains ?? []).map((c) => [c.chainId, c])), [data?.chains]);

  const storeMap = useMemo(() => new Map((data?.stores ?? []).map((s) => [s.storeId, s])), [data?.stores]);

  const rows = useMemo<TransformationRow[]>(
    () =>
      (data?.etlProcessings ?? []).map((etl) => {
        const chain = etl.chainId != null ? chainMap.get(etl.chainId) : undefined;
        const store = etl.storeId != null ? storeMap.get(etl.storeId) : undefined;
        return {
          ...etl,
          chainName: chain?.chainName ?? "",
          chainLegibleName: chain?.legibleName ?? "",
          storeName: store?.storeName ?? "",
          storeLegibleName: store?.legibleName ?? "",
        };
      }),
    [data?.etlProcessings, chainMap, storeMap],
  );

  const columns = useMemo<ColumnDef<TransformationRow, unknown>[]>(
    () => [
      {
        accessorKey: "etlProcessingId",
        header: t("transformations.list.columns.id"),
        size: 80,
        enableHiding: false,
        meta: {
          columnLabel: t("transformations.list.columns.id"),
          className: "pl-4",
          forceWidth: true,
          sticky: "left",
        },
        cell: ({ row }) => <RowId id={row.original.etlProcessingId} />,
      },
      {
        accessorKey: "shortName",
        header: t("transformations.list.columns.name"),
        meta: { columnLabel: t("transformations.list.columns.name") },
        cell: ({ row, table }) => {
          // Surface the key as a sub-line when its dedicated column is hidden from the view.
          const keyColumnVisible = table.getColumn("key")?.getIsVisible() ?? false;
          return (
            <div className="flex flex-col">
              <Link
                to="/ingress/transformations/$transformationId"
                params={{ transformationId: String(row.original.etlProcessingId) }}
                className="font-medium text-primary hover:underline w-fit"
              >
                {row.original.description}
              </Link>
              {!keyColumnVisible && <CopyableKey value={row.original.shortName} />}
            </div>
          );
        },
      },
      {
        id: "key",
        header: t("transformations.list.columns.key"),
        enableSorting: false,
        meta: { columnLabel: t("transformations.list.columns.key"), defaultHidden: true },
        cell: ({ row }) => <KeyChip label={row.original.shortName} />,
      },
      {
        id: "target",
        header: t("transformations.list.columns.target"),
        enableSorting: false,
        meta: { columnLabel: t("transformations.list.columns.target") },
        cell: ({ row }) => {
          if (row.original.storeName) {
            return <StoreLink storeName={row.original.storeName} legibleName={row.original.storeLegibleName} />;
          }
          if (row.original.chainName) {
            return <ChainLink chainName={row.original.chainName} legibleName={row.original.chainLegibleName} />;
          }
          return "—";
        },
      },
      {
        accessorKey: "outputImportTypes",
        header: t("transformations.list.columns.outputTypes"),
        enableSorting: false,
        meta: { columnLabel: t("transformations.list.columns.outputTypes") },
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.outputImportTypes.map((type) => (
              <ImportTypeBadge key={type} type={type} />
            ))}
          </div>
        ),
      },
      {
        accessorKey: "createdAt",
        header: t("transformations.list.columns.createdAt"),
        meta: { columnLabel: t("transformations.list.columns.createdAt") },
        cell: ({ row }) => <DateValue value={row.original.createdAt} />,
      },
      {
        id: "isActive",
        header: t("transformations.list.columns.status"),
        size: 80,
        enableSorting: false,
        meta: { columnLabel: t("transformations.list.columns.status") },
        cell: ({ row }) => <StatusIndicator active={row.original.isActive} />,
      },
      {
        id: "actions",
        size: 50,
        meta: { forceWidth: true, sticky: "right" },
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => (
          <TransformationActionsMenu
            isActive={row.original.isActive}
            onToggleActive={() => setToggleEtl(row.original)}
            onImportData={() => setImportEtl(row.original)}
          />
        ),
      },
    ],
    [t],
  );

  return (
    <div>
      <PageHeader title={t("transformations.title")} />
      <div className="p-6">
        <DataTable
          storageKey="transformations-list"
          columns={columns}
          data={rows}
          totalCount={data?.count ?? 0}
          isLoading={isLoading}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          search={search}
          onSearchChange={setSearch}
          searchDebounceMs={1000}
          onRefresh={refetch}
          isRefreshing={isFetching}
          onExport={handleExport}
          searchPlaceholder={t("transformations.list.searchPlaceholder")}
          filters={
            <>
              <FilterSelect
                label={t("transformations.list.filter.chain")}
                value={chainFilter}
                onChange={(v) => {
                  setChainFilter(v);
                  setStoreFilter("");
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                options={(context?.chains ?? []).map((c) => ({ label: c.display, value: c.value }))}
                searchPlaceholder={t("stores.form.searchChains")}
                emptyText={t("stores.form.noChains")}
                loading={contextLoading}
              />
              <FilterSelect
                label={t("transformations.list.filter.store")}
                value={storeFilter}
                onChange={(v) => {
                  setStoreFilter(v);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                options={(context?.stores ?? []).map((s) => ({ label: s.display, value: s.value }))}
                searchPlaceholder={t("users.list.filter.searchStores")}
                emptyText={t("users.list.filter.noStores")}
                loading={contextLoading}
              />
              <FilterToggle
                active={onlyActive}
                onChange={() => {
                  setOnlyActive((prev) => !prev);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                icon={Filter}
                label={t("common.active")}
              />
              {isFiltered && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:text-foreground"
                  onClick={clearFilters}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </>
          }
          toolbar={
            // Create Transformation is still WIP (mocked) — debug-mode only for now.
            canEdit && debugMode ? (
              <Button onClick={() => toast.warning(t("common.workInProgress"))}>
                <DatabasePlusIcon className="h-4 w-4" />
                <Construction className="h-4 w-4" />
                {t("transformations.list.addButton")}
              </Button>
            ) : null
          }
        />
      </div>

      <ToggleEtlActiveDialog etl={toggleEtl} onClose={() => setToggleEtl(null)} />
      <ImportDataDialog
        open={importEtl != null}
        onOpenChange={(o) => {
          if (!o) setImportEtl(null);
        }}
        transformation={importEtl ?? undefined}
      />
    </div>
  );
}
